import { useState, useEffect } from "react";

import { Button, Modal, Typography } from "antd";
import { DownloadOutlined, LeftCircleOutlined } from "@ant-design/icons";
import moment from "moment";

import { ErrorResponse, UserModel } from "utils/types";
import notifier from "utils/notifier";
import { sortArrayByKey, getDisplayCompanyId } from "utils/helpers";
import { ORGANIZATION_NAME } from "utils/objects";

import { RafScoreDetailsResponse, RafScoreListResponse } from "store/eligibility/rafScore/actions";

import TableResult from "components/Common/TableResult";
import constants from 'constants/index';

interface RiskScoreProvidersTableProps {
  getRafScoreList(userModal: UserModel): any,
  getRafScoreDetail(providerKeyId: string): any,
  getFile(providerKeyId: string): any,
  isFetchingList: boolean,
  isFetching: boolean,
  isFetchingFile: boolean
}

const { Title } = Typography;
const { PROV_RAF_TITLE, CURRENT_RAF_TITLE } = constants;

const RiskScoreProvidersTable = (props: RiskScoreProvidersTableProps) => {

  const [ipaDataSource, setIpaDataSource] = useState<any>([]);
  const [resultTotal, setResultTotal] = useState(0);
  const [isRiskScoreModalVisible, setIsRiskScoreModalVisible] = useState(false);
  const [riskScoreDataSource, setRiskScoreDataSource] = useState<any>([]);
  const [providerName, setProviderName] = useState('');
  const [providerKeyId, setProviderKeyId] = useState('');

  const columns = [
    {
      title: "IPA",
      key: "IPA",
      dataIndex: "IPA",
      render: (text: string) => getDisplayCompanyId(text)
    },
    {
      title: "PROVIDER",
      key: "ProviderKeyId",
      dataIndex: "ProviderKeyId",
      render: (_: string, record: any) =>
        <Button
          type="link"
          onClick={() => getProvider(record.ProviderKeyId, record.Provider)}>
          { record.Provider.split(',').join(', ') }
        </Button>
    }
  ];

  const rafScoreDetailColumns = [
    {
      title: "IPA",
      key: "IPA",
      dataIndex: "IPA",
    },
    {
      title: "MEMBER",
      key: "MemberID",
      dataIndex: "MemberID",
    },
    {
      title: "NAME",
      key: "MemberName",
      dataIndex: "MemberName",
    },
    {
      title: "GENDER",
      key: "Gender",
      dataIndex: "Gender",
    },
    {
      title: "DOB",
      key: "dob",
      dataIndex: "dob",
      render: (_: string, record: any) => record.DOB ? moment(record.DOB).format('MM/DD/YYYY'): ''
    },
    {
      title: "HEALTH PLAN",
      key: "HealthPlan",
      dataIndex: "HealthPlan",
    },
    {
      title: "OPTION",
      key: "Option",
      dataIndex: "Option",
    },
    {
      title: <>{PROV_RAF_TITLE}</>,
      key: "nov",
      dataIndex: "PrevRAFScore",
      render: (_: string, record: any) => record.PrevRAFScore ? record.PrevRAFScore : '0.00',
    },
    {
      title: <>{CURRENT_RAF_TITLE}</>,
      key: "RAFScore",
      dataIndex: "RAFScore",
    }
  ]

  const getRafScoreList = () => {
    const userModel = {
      UserId: sessionStorage.user_id,
      UserRole: sessionStorage.user_role,
    };

    return props.getRafScoreList(userModel)
      .then((response: RafScoreListResponse[]) => {
        const sortedByProvider = sortArrayByKey(response, 'Provider');
        setResultTotal(sortedByProvider.length);
        setIpaDataSource(sortedByProvider);
      })
      .catch((err: ErrorResponse) =>
        notifier.error( err.response.data.Message || "Something went wrong, please contact a system administrator.")
      );
  };

  const getProvider = (providerKeyId: string, providerName: string) => {
    setIsRiskScoreModalVisible(true);
    setProviderKeyId(providerKeyId);
    setProviderName(providerName.split(',').join(', '));
    return props.getRafScoreDetail(providerKeyId)
      .then((response: RafScoreDetailsResponse[]) => {
        setRiskScoreDataSource(response);
      })
      .catch((err: ErrorResponse) =>
        notifier.error( err.response.data.Message || "Something went wrong, please contact a system administrator.")
      );
  }

  const handleCancel = () => {
    setIsRiskScoreModalVisible(false);
  }

  const exportExcel = () => {
    return props.getFile(providerKeyId)
      .then((response: any) => {
        const file = new Blob([response], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
        const fileURL = URL.createObjectURL(file);
        const anchor = document.createElement("a");
        anchor.download = providerName + '.xlsx';
        anchor.href = fileURL;
        anchor.click();
        notifier.success('Success! File has downloaded.');
      })
      .catch((err: ErrorResponse) =>
        notifier.error( err.response.data.Message || "Something went wrong, please contact a system administrator.")
      );
  }

  useEffect(() => {
    getRafScoreList();
  }, []);

  return (
    <>
      <TableResult
        columns={columns}
        dataSource={ipaDataSource}
        showSizeChanger={true}
        loading={props.isFetchingList}
      />

      <Modal
        visible={isRiskScoreModalVisible}
        width={1000}
        onCancel={handleCancel}
        footer={[
          <div className="text-center mt-2 mb-2">
            Copyright {ORGANIZATION_NAME}, Inc. © {moment().year()}
          </div>
        ]}
      >
        <div className="row">
          <div className="col text-center">
            <Title level={4}>RAF Score Detail</Title>
            <Button
              type="primary"
              style={{
                float: 'left',
                background: "orange",
                borderColor: "orange"
              }}
              loading={props.isFetchingFile} onClick={() => handleCancel()}
              icon={<LeftCircleOutlined />}>
              Back
            </Button>
            <Button type="primary" style={{float: 'right'}} shape="round" loading={props.isFetchingFile} onClick={() => exportExcel()} icon={<DownloadOutlined />}>
              Export Excel
            </Button>
          </div>
        </div>

        <hr />

        <Title level={5}>{providerName}</Title>
        <TableResult
          columns={rafScoreDetailColumns}
          dataSource={riskScoreDataSource}
          loading={props.isFetching}
          defaultPageSize={10}
          showSizeChanger={true}
        />

      </Modal>
    </>
  );
};

export default RiskScoreProvidersTable;
